import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantStudioCategory,
  createTenantStudioLocation,
  createTenantStudioSeason,
  deleteTenantStudioCategory,
  deleteTenantStudioLocation,
  deleteTenantStudioSeason,
  getTenantStudioSettings,
  updateTenantStudioCategory,
  updateTenantStudioLocation,
  updateTenantStudioSeason,
} from '../lib/studioSettings';
import type {
  TenantStudioCategory,
  TenantStudioCategoryPayload,
  TenantStudioLocation,
  TenantStudioLocationPayload,
  TenantStudioSeason,
  TenantStudioSeasonPayload,
  TenantStudioSettings,
} from '../types';

export const tenantStudioSettingsKeys = {
  all: ['tenant-studio-settings'] as const,
  settings: () => [...tenantStudioSettingsKeys.all, 'settings'] as const,
};

export function useTenantStudioSettings(enabled: boolean = true) {
  return useQuery<TenantStudioSettings>({
    queryKey: tenantStudioSettingsKeys.settings(),
    queryFn: getTenantStudioSettings,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useCreateTenantStudioLocation() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioLocation, Error, TenantStudioLocationPayload>({
    mutationFn: createTenantStudioLocation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useUpdateTenantStudioLocation() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioLocation, Error, { locationId: number; payload: TenantStudioLocationPayload }>({
    mutationFn: ({ locationId, payload }) => updateTenantStudioLocation(locationId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useDeleteTenantStudioLocation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTenantStudioLocation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useCreateTenantStudioSeason() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioSeason, Error, TenantStudioSeasonPayload>({
    mutationFn: createTenantStudioSeason,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useUpdateTenantStudioSeason() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioSeason, Error, { seasonId: number; payload: TenantStudioSeasonPayload }>({
    mutationFn: ({ seasonId, payload }) => updateTenantStudioSeason(seasonId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useDeleteTenantStudioSeason() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTenantStudioSeason,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useCreateTenantStudioCategory() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioCategory, Error, TenantStudioCategoryPayload>({
    mutationFn: createTenantStudioCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useUpdateTenantStudioCategory() {
  const queryClient = useQueryClient();
  return useMutation<TenantStudioCategory, Error, { categoryId: number; payload: TenantStudioCategoryPayload }>({
    mutationFn: ({ categoryId, payload }) => updateTenantStudioCategory(categoryId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}

export function useDeleteTenantStudioCategory() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteTenantStudioCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantStudioSettingsKeys.all }),
  });
}
